'use client';

import { useEffect, useRef } from 'react';

type UseUrlChangeCallback = (url: string) => void;

// Store original history methods globally to prevent multiple patches
let originalPushState: History['pushState'] | null = null;
let originalReplaceState: History['replaceState'] | null = null;
let patchCount = 0;

export function useUrlChange(callback: UseUrlChangeCallback) {
  // Use useRef to store the latest callback without changing effect dependencies
  const callbackRef = useRef(callback);

  // Update the ref whenever the callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Effect to handle URL changes
  useEffect(() => {
    if (typeof window === 'undefined') {
      // Return an empty cleanup function for SSR
      return () => {};
    }

    let lastUrl = window.location.href;

    const observer = () => {
      const currentUrl = window.location.href;
      if (currentUrl !== lastUrl) {
        lastUrl = currentUrl;
        // Use setTimeout to ensure the callback is executed after the URL change
        // Prevent useInsertEffect from being called immediately (some library is using it)
        setTimeout(() => {
          // Use the ref to ensure the latest callback is always called
          if (callbackRef.current) {
            callbackRef.current(currentUrl);
          }
        }, 0);
      }
    };

    // Only patch history methods once (first mount across all hook instances)
    if (patchCount === 0) {
      // Store original methods before patching
      originalPushState = window.history.pushState.bind(window.history);
      originalReplaceState = window.history.replaceState.bind(window.history);

      // Patch window.history.pushState to trigger a custom event
      const patchedPushState = (
        state: unknown,
        title: string,
        url?: string | URL | null,
      ) => {
        const result = originalPushState!(state, title, url);
        window.dispatchEvent(new Event('urlchange'));
        return result;
      };
      window.history.pushState = patchedPushState;
    }
    patchCount += 1;

    // Listen for our custom event + back/forward navigation
    window.addEventListener('urlchange', observer);
    window.addEventListener('popstate', observer);

    // Also poll every 100ms in case of missed events
    const interval = setInterval(observer, 100);

    // Return the cleanup function
    return () => {
      window.removeEventListener('urlchange', observer);
      window.removeEventListener('popstate', observer);
      clearInterval(interval);

      // Restore original methods when last instance unmounts
      patchCount -= 1;
      if (patchCount === 0 && originalPushState && originalReplaceState) {
        window.history.pushState = originalPushState;
        window.history.replaceState = originalReplaceState;
        originalPushState = null;
        originalReplaceState = null;
      }
    };
  }, []);
}
